/**
 * RoomBackground: renders the static, non-interactive backdrop of the
 * canvas — the room's true-to-scale outline, entrance marker, and any
 * fixed exclusion zones (columns, fixed bar, etc. — PRD §5.1).
 *
 * FEET_TO_PX (the feet-to-pixel scale every shape component shares) lives
 * in canvasConstants.ts, re-exported here for convenience since this is
 * the file most other canvas code already imports from.
 */

import { Group, Rect, Line, Text } from 'react-konva';
import type { RoomDefinition } from '../../domain/types';
import { FEET_TO_PX } from './canvasConstants';

export { FEET_TO_PX };

interface RoomBackgroundProps {
  room: RoomDefinition;
}

export function RoomBackground({ room }: RoomBackgroundProps) {
  const widthPx = room.widthFt * FEET_TO_PX;
  const heightPx = room.lengthFt * FEET_TO_PX;

  return (
    <Group>
      {/* Room floor — soft linen tone with a thin gold border, like a
          place card rather than a CAD drawing canvas. */}
      <Rect
        x={0}
        y={0}
        width={widthPx}
        height={heightPx}
        fill="#FBF8F2"
        stroke="#C9A35C"
        strokeWidth={2}
      />

      {/* Entrance marker */}
      <Group x={room.entrance.x * FEET_TO_PX} y={room.entrance.y * FEET_TO_PX}>
        <Line points={[-18, 0, 18, 0]} stroke="#6E8569" strokeWidth={4} lineCap="round" />
        <Text
          text="ENTRANCE"
          fontSize={10}
          fontFamily="Inter, sans-serif"
          fill="#6E8569"
          x={-30}
          y={8}
          width={60}
          align="center"
        />
      </Group>

      {/* Fixed exclusion zones — columns, built-in bar, doors, etc.
          Rendered with a hatch pattern to read clearly as "do not place
          here" rather than as a placeable object. Labels on tall,
          narrow zones (e.g. a long sliding door running the length of a
          wall) rotate 90° to read along the zone's long axis instead of
          being crammed illegibly into a narrow width. */}
      {room.exclusionZones.map((zone, i) => {
        const zoneWidthPx = zone.width * FEET_TO_PX;
        const zoneHeightPx = zone.height * FEET_TO_PX;
        const isTallAndNarrow = zoneHeightPx > zoneWidthPx * 2;

        return (
          <Group key={i} x={zone.x * FEET_TO_PX} y={zone.y * FEET_TO_PX} rotation={zone.rotation}>
            <Rect
              width={zoneWidthPx}
              height={zoneHeightPx}
              fill="#E9EEE8"
              stroke="#6E8569"
              strokeWidth={1}
              dash={[4, 4]}
            />
            {isTallAndNarrow ? (
              <Text
                text={zone.label}
                fontSize={9}
                fontFamily="Inter, sans-serif"
                fill="#6E8569"
                width={zoneHeightPx}
                align="center"
                listening={false}
                rotation={90}
                x={zoneWidthPx / 2}
                y={zoneHeightPx / 2}
                offsetX={zoneHeightPx / 2}
                offsetY={4.5}
              />
            ) : (
              <Text
                text={zone.label}
                fontSize={9}
                fontFamily="Inter, sans-serif"
                fill="#6E8569"
                width={zoneWidthPx}
                height={zoneHeightPx}
                align="center"
                verticalAlign="middle"
                listening={false}
              />
            )}
          </Group>
        );
      })}
    </Group>
  );
}
